
import React from 'react';
import { Button } from '@/components/ui/button';
import { Image, ImageOff, Upload, X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { useIsMobile } from '@/hooks/use-mobile';

interface CountryImageUploadProps {
  hasImage: boolean;
  imageUrl: string | null | undefined;
  onImageSelect: (file: File | null) => void;
  onImageRemove: () => void;
}

export const CountryImageUpload: React.FC<CountryImageUploadProps> = ({
  hasImage,
  imageUrl,
  onImageSelect,
  onImageRemove,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      onImageSelect(file);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-3 md:space-y-4">
      <div>
        <Label className="block mb-2 font-medium">Hero Background Image</Label>
        
        {hasImage && (
          <div className={`mb-3 md:mb-4 rounded-md overflow-hidden border ${isMobile ? 'h-24 md:h-32' : 'h-40'}`}>
            <img
              src={imageUrl || ''}
              alt="Country hero background"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className={`flex items-center gap-2 ${isMobile ? 'flex-col' : ''}`}>
          <Button
            type="button"
            variant="outline"
            onClick={handleBrowseClick}
            className={isMobile ? "w-full mb-1" : ""}
            size={isMobile ? "sm" : "default"}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />
            <Upload size={isMobile ? 14 : 16} className="mr-2" />
            {hasImage ? "Replace Image" : "Upload Image"}
          </Button>

          {hasImage && (
            <Button
              type="button"
              variant="destructive"
              onClick={onImageRemove}
              className={isMobile ? "w-full" : ""}
              size={isMobile ? "sm" : "default"}
            >
              <X size={isMobile ? 14 : 16} className="mr-2" />
              Remove Image
            </Button>
          )}
        </div>
      </div>

      {!hasImage && (
        <div className="bg-muted/50 p-3 md:p-4 rounded-md flex items-center justify-center h-20 md:h-24">
          <div className="flex flex-col items-center text-muted-foreground">
            <ImageOff size={isMobile ? 20 : 24} className="mb-2" />
            <p className="text-xs md:text-sm">No hero image selected</p>
          </div>
        </div>
      )}
    </div>
  );
};
